/*
 * Membership offer: the Healing Secrets of the World gift switch beside the buy
 * button, and the members' invitation pop-up.
 *
 * <membership-gift> extends the product add-on (product-addon.js), so it is
 * placed above the GemPages buy button the same way and rides along on the
 * same /cart/add hook. What differs from the socks row:
 * - the switch never edits the cart: it decides whether the next add of this
 *   shoe brings the gift, stays on after an add, and an add skips the gift
 *   while it is already in the cart,
 * - whether the gift is in the cart is always read from /cart.js, never from
 *   the page HTML, which Shopify can serve with an older cart,
 * - it only joins adds of this page's own product, never a cart-drawer upsell,
 * - it goes in with the Loop selling plan when one is configured.
 *
 * Tracking: every interaction is pushed to window.dataLayer and dispatched on
 * window as `membership:<name>` (gift_toggle_on, gift_toggle_off, gift_expand,
 * gift_added, popup_view, popup_add, popup_decline, popup_close).
 */
;(() => {
  const Base = window.ProductAddon
  if (!Base || customElements.get('membership-gift')) return

  const INTERNAL = 'x-addon-internal'
  const SOURCE = 'membership-gift'

  const storage = {
    get(area, key) {
      try {
        return window[area].getItem(key)
      } catch (e) {
        return null
      }
    },
    set(area, key, value) {
      try {
        window[area].setItem(key, value)
      } catch (e) {}
    },
  }

  function track(name, detail = {}) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({event: name, ...detail})
    window.dispatchEvent(new CustomEvent(`membership:${name}`, {detail}))
  }

  function routes(path) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'
    return `${root}${path}`
  }

  class MembershipGift extends Base {
    connectedCallback() {
      if (this.initialised) return

      /* The switch survives size and colour changes and page reloads within
         the session; the store default only applies on the first view. */
      /* Without the visible switch only a yes in the pop-up turns the gift on, so
         a choice saved from the switch in an earlier session does not count. */
      const headless = this.classList.contains('mgift--headless')
      const saved = headless ? null : storage.get('sessionStorage', 'mgift:on')
      this.restored = saved === null ? this.dataset.defaultOn === 'true' : saved === 'true'

      super.connectedCallback()
      if (!this.toggle) return

      this.productVariants = (this.dataset.productVariants || '').split(',').map(Number).filter(Boolean)
      this.giftVariant = Number(this.dataset.variantId)
      this.inCart = false
      this.setChecked(this.restored, {silent: true})

      this.expander = this.querySelector('[data-gift-expand]')
      this.perks = this.querySelector('.mgift__perks')
      if (this.expander) this.expander.addEventListener('click', () => this.setExpanded(!this.expanded))
      const coverProxy = this.querySelector('[data-gift-expand-proxy]')
      if (coverProxy && this.expander) coverProxy.addEventListener('click', () => this.expander.click())

      this.watchButtons()
      this.watchCart()
      this.readCart().catch(() => {})
    }

    get expanded() {
      return this.expander.getAttribute('aria-expanded') === 'true'
    }

    setExpanded(open) {
      this.expander.setAttribute('aria-expanded', open ? 'true' : 'false')
      this.perks.inert = !open
      this.classList.toggle('is-expanded', open)
      if (open) track('gift_expand')
    }

    /* The switch only decides whether the next add of this shoe brings the gift; it never edits the cart. */
    setChecked(on, {silent = false} = {}) {
      super.setChecked(on)
      this.classList.toggle('is-on', on)
      document.documentElement.classList.toggle('mgift-on', on)
      storage.set('sessionStorage', 'mgift:on', on ? 'true' : 'false')
      if (!silent) track(on ? 'gift_toggle_on' : 'gift_toggle_off')
    }

    readCart() {
      return fetch(routes('cart.js'), {headers: {Accept: 'application/json'}})
        .then((response) => response.json())
        .then((cart) => {
          this.inCart = cart.items.some((item) => item.variant_id === this.giftVariant)
          return cart
        })
    }

    /*
     * Adds the gift on its own when this shoe is already in the cart and the
     * gift is not: for the returning-visitor pop-up, a deliberate yes.
     */
    addWithShoeInCart() {
      return this.readCart().then((cart) => {
        const hasShoe = cart.items.some((item) => this.productVariants.includes(item.variant_id))
        if (this.inCart || !hasShoe) return
        this.inCart = true
        track('gift_added')
        return this.post('cart/add.js', {items: [this.cartItem()]})
      })
    }

    post(path, body) {
      return fetch(routes(path), {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'application/json', [INTERNAL]: '1'},
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .then(() => this.readCart())
        .then((cart) => this.refreshCart(cart))
    }

    refreshCart(cart) {
      const drawer = document.querySelector('m-cart-drawer')
      if (drawer && typeof drawer.onCartDrawerUpdate === 'function') drawer.onCartDrawerUpdate()
      const bus = window.MinimogEvents
      const event =
        window.MinimogTheme && window.MinimogTheme.pubSubEvents && window.MinimogTheme.pubSubEvents.cartUpdate
      if (bus && event && typeof bus.emit === 'function') bus.emit(event, {...cart, source: SOURCE})
    }

    /* Not named matches(): that would shadow Element.matches, which other scripts call with a selector. */
    isThisProduct(variantIds) {
      return variantIds.some((id) => this.productVariants.includes(id))
    }

    /*
     * Before an add of this shoe: read the real cart, so a gift already in it is
     * never added twice, then, with the gift off, offer it once in the pop-up.
     */
    prepare(variantIds) {
      if (!this.isThisProduct(variantIds)) return
      return this.readCart().then(() => {
        if (this.checked || this.inCart) return
        const popup = document.querySelector('membership-popup')
        if (!popup || !popup.canShow('atc')) return
        return popup.ask('atc').then((choice) => {
          if (choice === 'add') this.setChecked(true)
        })
      })
    }

    wantsAdd(variantIds) {
      return this.checked && !this.inCart && this.isThisProduct(variantIds)
    }

    cartItem() {
      const item = {id: Number(this.dataset.variantId), quantity: 1}
      const plan = Number(this.dataset.sellingPlan)
      if (plan) item.selling_plan = plan
      return item
    }

    onAdded() {
      this.inCart = true
      track('gift_added')
    }

    /*
     * "Free gift included" as a second line inside every buy button. GemPages
     * renders its button after its own scripts run, so watch for it for a
     * while. The button keeps the height it has without the note (see
     * lockHeight), so switching the gift on does not move the page.
     */
    watchButtons() {
      const selector = this.dataset.atcSelector
      const text = this.dataset.atcNote
      if (!selector || !text) return

      const decorate = () => {
        let buttons = []
        try {
          buttons = document.querySelectorAll(selector)
        } catch (e) {
          return
        }
        buttons.forEach((button) => {
          if (button.querySelector('.mgift-atc-note')) return
          const note = document.createElement('span')
          note.className = 'mgift-atc-note'
          note.textContent = text
          button.classList.add('has-mgift-note')
          button.appendChild(note)
          lockHeight(button)
        })
      }

      /* Measures the button without the note and keeps that as its height. */
      const lockHeight = (button) => {
        button.classList.add('mgift-measuring')
        const height = button.offsetHeight
        button.classList.remove('mgift-measuring')
        if (height) button.style.setProperty('--mgift-atc-h', `${height}px`)
      }

      decorate()
      const observer = new MutationObserver(decorate)
      observer.observe(document.body, {childList: true, subtree: true})
      setTimeout(() => observer.disconnect(), 15000)

      let resizeTimer
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer)
        resizeTimer = setTimeout(() => document.querySelectorAll('.has-mgift-note').forEach(lockHeight), 200)
      })
    }

    /*
     * Keep `inCart` honest when the cart changes elsewhere. A gift removed while
     * other items stay (in the drawer, or by the cart guard) also switches the
     * toggle off; an emptied cart leaves it as it was.
     */
    watchCart() {
      const bus = window.MinimogEvents
      const event =
        window.MinimogTheme && window.MinimogTheme.pubSubEvents && window.MinimogTheme.pubSubEvents.cartUpdate
      if (!bus || !event || typeof bus.subscribe !== 'function') return

      bus.subscribe(event, (detail) => {
        if (detail && detail.source === SOURCE) return
        const wasInCart = this.inCart
        this.readCart()
          .then((cart) => {
            if (wasInCart && !this.inCart && cart.item_count > 0) this.setChecked(false)
          })
          .catch(() => {})
      })
    }
  }

  /*
   * The members' invitation. Two triggers, chosen in theme settings so they
   * can be tested separately:
   * - atc: add to cart with the gift off, once per session. The add waits for
   *   the answer; every answer lets it through.
   * - returning: second or later session, after a delay or half-page scroll,
   *   at most once per cooldown, never with the gift already on or in the cart.
   */
  class MembershipPopup extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return
      this.initialised = true

      this.dialog = this.querySelector('dialog')
      this.trigger = this.dataset.trigger
      this.watchTest()

      this.querySelector('[data-popup-add]').addEventListener('click', () => this.answer('add'))
      this.querySelector('[data-popup-decline]').addEventListener('click', () => this.answer('decline'))
      this.querySelector('[data-popup-close]').addEventListener('click', () => this.answer('close'))
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault()
        this.answer('close')
      })
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.answer('close')
      })

      this.fillDeadline()
      this.countVisit()
      if (this.trigger === 'returning' || this.trigger === 'both') this.armReturning()
    }

    get gift() {
      return document.querySelector('membership-gift')
    }

    canShow(mode) {
      if (storage.get('sessionStorage', 'mpop:shown')) return false
      if (this.dataset.igTest && !this.inTestVariant) return false
      return this.trigger === mode || this.trigger === 'both'
    }

    /*
     * With an Intelligems test id set, only visitors in a non-control group of
     * that test see the pop-up. Until Intelligems has answered, nobody does.
     */
    watchTest() {
      const id = this.dataset.igTest
      if (!id) return
      this.inTestVariant = false
      const read = () => {
        const user = window.igData && window.igData.user
        if (!user || typeof user.getTestGroup !== 'function') return
        const group = user.getTestGroup(id)
        this.inTestVariant = !!group && !group.isControl
      }
      read()
      window.addEventListener('ig:ready', read)
    }

    showTexts(mode) {
      this.querySelector('[data-decline-atc]').hidden = mode !== 'atc'
      this.querySelector('[data-decline-returning]').hidden = mode === 'atc'
      this.querySelector('[data-text-atc]').hidden = mode !== 'atc'
      this.querySelector('[data-text-returning]').hidden = mode === 'atc'
    }

    /* Theme editor only: open without counting a view, storing anything or tracking. */
    preview() {
      this.previewing = true
      this.showTexts('atc')
      this.startTimer()
      this.show()
    }

    endPreview() {
      this.previewing = false
      this.hide()
      clearInterval(this.ticker)
    }

    show() {
      this.finishHide()
      if (!this.dialog.open) this.dialog.showModal()
      document.documentElement.classList.add('mpop-open')
    }

    /* On phones the sheet slides down first (CSS); close once that animation ends, or straight away without one. */
    hide() {
      document.documentElement.classList.remove('mpop-open')
      if (!this.dialog.open || this.hiding) return
      const animation = getComputedStyle(this.dialog).getPropertyValue('--mpop-sheet-out').trim()
      if (!animation) return this.dialog.close()

      this.hiding = true
      this.dialog.classList.add('is-closing')
      const onEnd = (event) => {
        if (event.target === this.dialog && event.animationName === 'mpop-sheet-out') this.finishHide()
      }
      this.dialog.addEventListener('animationend', onEnd)
      this.hideFallback = setTimeout(() => this.finishHide(), parseFloat(animation) + 150)
      this.hideCleanup = () => this.dialog.removeEventListener('animationend', onEnd)
    }

    finishHide() {
      if (!this.hiding) return
      this.hiding = false
      clearTimeout(this.hideFallback)
      this.hideCleanup()
      this.dialog.classList.remove('is-closing')
      this.dialog.close()
    }

    ask(mode) {
      this.mode = mode
      this.showTexts(mode)
      storage.set('sessionStorage', 'mpop:shown', '1')
      if (mode === 'returning') storage.set('localStorage', 'mpop:last', String(Date.now()))

      this.startTimer()
      this.show()
      track('popup_view', {trigger: mode})

      return new Promise((resolve) => {
        this.resolve = resolve
      })
    }

    answer(choice) {
      if (this.previewing) return this.endPreview()
      if (!this.dialog.open || this.hiding) return
      this.hide()
      clearInterval(this.ticker)
      track(`popup_${choice}`, {trigger: this.mode})

      if (this.mode === 'returning' && choice === 'add') this.acceptReturning()
      if (this.resolve) this.resolve(choice)
      this.resolve = null
    }

    /* A yes to the returning-visitor pop-up adds the gift straight away when this shoe is already in the cart. */
    acceptReturning() {
      const gift = this.gift
      if (!gift) return
      gift.setChecked(true)
      gift.addWithShoeInCart().catch(() => {})
    }

    countVisit() {
      if (storage.get('sessionStorage', 'mpop:session')) return
      storage.set('sessionStorage', 'mpop:session', '1')
      const visits = Number(storage.get('localStorage', 'mpop:visits')) || 0
      storage.set('localStorage', 'mpop:visits', String(visits + 1))
    }

    armReturning() {
      const visits = Number(storage.get('localStorage', 'mpop:visits')) || 0
      const last = Number(storage.get('localStorage', 'mpop:last')) || 0
      const cooldown = (Number(this.dataset.cooldown) || 7) * 864e5
      if (visits < 2 || Date.now() - last < cooldown) return

      const fire = () => {
        window.removeEventListener('scroll', onScroll)
        clearTimeout(timer)
        const gift = this.gift
        if (!this.canShow('returning') || this.dialog.open || (gift && (gift.checked || gift.inCart))) return
        this.ask('returning')
      }
      const onScroll = () => {
        const depth = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight
        if (depth >= 0.5) fire()
      }
      const timer = setTimeout(fire, (Number(this.dataset.delay) || 0) * 1000)
      window.addEventListener('scroll', onScroll, {passive: true})
    }

    /* Counts down from the configured minutes, from the first time the
       pop-up opens in this session; it does not restart on a second view. */
    startTimer() {
      const clock = this.querySelector('[data-timer-clock]')
      if (!clock) return
      let end = Number(storage.get('sessionStorage', 'mpop:timer'))
      if (!end) {
        end = Date.now() + (Number(this.dataset.timer) || 10) * 6e4
        storage.set('sessionStorage', 'mpop:timer', String(end))
      }
      const render = () => {
        const left = Math.max(0, Math.round((end - Date.now()) / 1000))
        clock.textContent = `${String(Math.floor(left / 60)).padStart(2, '0')}:${String(left % 60).padStart(2, '0')}`
        if (!left) clearInterval(this.ticker)
      }
      render()
      clearInterval(this.ticker)
      this.ticker = setInterval(render, 1000)
    }

    fillDeadline() {
      const label = this.querySelector('[data-deadline]')
      if (!label || !label.textContent.includes('[date]')) return
      const date = new Date(Date.now() + (Number(this.dataset.deadlineDays) || 0) * 864e5)
      const month = date.toLocaleString('en-US', {month: 'short'})
      const abbreviated = month.length <= 4 && month !== 'May' && month !== 'June' && month !== 'July'
      label.textContent = label.textContent.replace('[date]', `${month}${abbreviated ? '.' : ''} ${date.getDate()}`)
    }
  }

  /*
   * Theme editor: the invitation opens while its section or one of its perks
   * is selected, and again after each settings change re-renders it, so the
   * merchant sees what they are editing.
   */
  if (window.Shopify && window.Shopify.designMode) {
    let selected = false
    const popupIn = (target) => target && target.querySelector && target.querySelector('membership-popup')

    document.addEventListener('shopify:section:select', (event) => {
      const popup = popupIn(event.target)
      selected = !!popup
      if (popup) popup.preview()
    })
    document.addEventListener('shopify:block:select', (event) => {
      const popup = popupIn(event.target.closest('.shopify-section'))
      if (!popup) return
      selected = true
      popup.preview()
    })
    document.addEventListener('shopify:section:deselect', (event) => {
      const popup = popupIn(event.target)
      if (!popup) return
      selected = false
      popup.endPreview()
    })
    document.addEventListener('shopify:section:load', (event) => {
      const popup = popupIn(event.target)
      if (popup && selected) popup.preview()
    })
  }

  customElements.define('membership-gift', MembershipGift)
  if (!customElements.get('membership-popup')) customElements.define('membership-popup', MembershipPopup)
})()
